import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import Pusher from 'pusher-js';
import { pullAt } from 'lodash-es';

/**
 * Destructure `accessToken` and transform to a string
 */
function toAuthorizationScheme(_ref) {
  var {
    scheme,
    token
  } = _ref;
  return scheme + " " + token;
}
/**
 * Using the `accessToken` field, generate the value for a request
 * Authorization header
 */
function getAuthorizationHeaderFor(accessToken) {
  if (accessToken === void 0) {
    accessToken = "";
  }
  if (typeof accessToken === "string") return "JWT " + accessToken;
  return toAuthorizationScheme(accessToken);
}

/**
 * The Socket class provides an abstraction over the pusher-js api,
 * keeping an internal state of Channel and Event subscriptions, with
 * methods to connect to a channel, subscribe to an event, manage
 * unsuccessful connections etc.
 */
class Socket {
  constructor(token, PusherImplementation, SOCKET_APP_KEY, SOCKET_APP_CLUSTER, SOCKET_AUTH_URL) {
    if (PusherImplementation === void 0) {
      PusherImplementation = Pusher;
    }
    this._pusher = new PusherImplementation(SOCKET_APP_KEY, {
      cluster: SOCKET_APP_CLUSTER,
      auth: {
        headers: {
          Authorization: getAuthorizationHeaderFor(token)
        }
      },
      authEndpoint: SOCKET_AUTH_URL + "/pusher/auth",
      forceTLS: true
    });
    this._pusher.connection.bind('error', function onError(err) {
      console.error(err);
    });
    this._channels = {};
    /** We clear the channels when disconnected,
     * because we are going to reconnect those */
    this._pusher.connection.bind('unavailable', () => {
      this._channels = {};
    });
    this._pusher.connection.bind('disconnected', () => {
      this._channels = {};
    });
  }
  isPusherConnected() {
    return this._pusher.connection.state === 'connected';
  }
  subscribeEvent(event, channelId, callback) {
    var response = this.subscribeChannel({
      channel: event.channel,
      channelId
    });
    if (!response) return;
    response == null || response.bind(event, callback);
    return {
      trigger: (event, data) => response == null ? void 0 : response.trigger(event, data)
    };
  }
  unsubscribeEvent(event, channelId, callback) {
    if (!this.isPusherConnected()) return;
    var channelName = event.getChannel(channelId);
    if (!this._channels[channelName]) {
      /** nothing to unsubscribe from */
      return;
    }
    /** we unbind the callback */
    this._channels[channelName].channel.unbind(event.name, callback);
    /** we remove it from the list of callback that exist */
    pullAt(this._channels[channelName].binded, this._channels[channelName].binded.lastIndexOf(callback));
    /** if we don't have any listener left, unsubscribe channel */
    if (this._channels[channelName].binded.length === 0) {
      this.unsubscribeChannel({
        channel: event.channel,
        channelId
      });
    }
  }
  subscribeChannel(_ref) {
    var {
      channel,
      channelId
    } = _ref;
    if (!this.isPusherConnected()) {
      console.warn('Pusher not connected');
      return;
    }
    var channelName = channel == null ? void 0 : channel.getName(channelId);
    /** if we haven't already subscribed, we add it */
    if (!this._channels[channelName]) {
      this._channels[channelName] = {
        channel: this._pusher.subscribe(channelName),
        binded: []
      };
    }
    var pusherChannel = this._channels[channelName].channel;
    var bind = (event, cb) => {
      pusherChannel.bind(event.name, cb);
      this._channels[channelName].binded.push(cb);
    };
    var trigger = (event, data) => {
      return pusherChannel.trigger(event.name, data);
    };
    return {
      bind,
      trigger
    };
  }
  unsubscribeChannel(_ref2) {
    var {
      channel,
      channelId
    } = _ref2;
    if (!this.isPusherConnected()) return;
    var channelName = channel.getName(channelId);
    /** nothing to unsubscribe from */
    if (!this._channels[channelName]) {
      return;
    }
    this._channels[channelName].channel.unsubscribe();
    delete this._channels[channelName];
  }
  disconnect() {
    this._pusher.disconnect();
  }
  onConnected(callback) {
    this._pusher.connection.bind('connected', callback);
  }
  onDisconnected(callback) {
    this._pusher.connection.bind('unavailable', callback);
    this._pusher.connection.bind('disconnected', callback);
  }
}

var SocketContext = /*#__PURE__*/createContext({
  ready: false,
  socket: undefined
});
function SocketProvider(_ref) {
  var {
    children,
    SocketImplementation = Socket,
    token,
    socketCredentials
  } = _ref;
  var [value, setValue] = useState({
    ready: false,
    socket: undefined
  });
  var {
    PUSHER_APP_KEY,
    PUSHER_APP_CLUSTER,
    PUSHER_AUTH_URL
  } = socketCredentials;
  useEffect(() => {
    if (token) {
      var socket = new SocketImplementation(token, Pusher, PUSHER_APP_KEY, PUSHER_APP_CLUSTER, PUSHER_AUTH_URL);
      socket.onConnected(() => {
        setValue({
          socket,
          ready: true
        });
      });
      socket.onDisconnected(() => {
        setValue({
          socket,
          ready: false
        });
      });
      return () => socket.disconnect();
    }
  }, [token, SocketImplementation, PUSHER_APP_KEY, PUSHER_APP_CLUSTER, PUSHER_AUTH_URL]);
  return /*#__PURE__*/React.createElement(SocketContext.Provider, {
    value: value
  }, children);
}

class MockSocket {
  constructor() {
    this._channels = {};
  }
  fireEvent(_ref) {
    var {
      event,
      channelId,
      data
    } = _ref;
    var channelName = this._getChannelName(event, channelId);
    var {
      binded
    } = this._channels[channelName] || {
      binded: []
    };
    binded.forEach(callback => {
      callback(data);
    });
  }
  _getChannelName(event, channelId) {
    return event.getChannel(channelId) + event.name;
  }
  _getName(channel, channelId) {
    return channel.getName(channelId) + channel.name;
  }
  subscribeEvent(event, channelId, callback) {
    var channelName = this._getChannelName(event, channelId);
    /** if we haven't already subscribed, we add it */
    if (!this._channels[channelName]) {
      this._channels[channelName] = {
        binded: [callback]
      };
    } else {
      this._channels[channelName].binded.push(callback); // we keep track of the callback we bind
    }
    return {
      trigger: () => true
    };
  }
  unsubscribeEvent(event, channelId, callback) {
    var channelName = this._getChannelName(event, channelId);
    if (!this._channels[channelName]) {
      /** nothing to unsubscribe from */
      return;
    }
    /** we remove it from the list of callback that exist */
    pullAt(this._channels[channelName].binded, this._channels[channelName].binded.lastIndexOf(callback));
    /** if we don't have any listener left, unsubscribe channel */
    if (this._channels[channelName].binded.length === 0) {
      delete this._channels[channelName];
    }
  }
  disconnect() {
    this._channels = {};
  }
  onConnected(callback) {
    this._onConnectedCallback = callback;
  }
  onDisconnected(callback) {
    this._onDisconnectedCallback = callback;
  }
  manualDisconnect() {
    this.disconnect();
    if (this._onDisconnectedCallback) this._onDisconnectedCallback();
  }
  manualConnect() {
    if (this._onConnectedCallback) this._onConnectedCallback();
  }
}

function MockedSocketProvider(_ref) {
  var {
    children,
    socket,
    ready = true
  } = _ref;
  var [value] = useState({
    ready,
    socket: socket || new MockSocket()
  });
  return /*#__PURE__*/React.createElement(SocketContext.Provider, {
    value: value
  }, children);
}

/**
 * Events are the primary method of packaging messages in the Channels system.
 * We use events to indicate what type of message / information is being sent
 * within a channel. Events could be like pin-message or client-typing, which give
 * an enforced naming convention (usually past tense).
 *
 * @param Channel The channel an event is being bound too
 * @param name The name of the event e.g. `client-typing`
 */
class Event {
  constructor(name, channel) {
    this.channel = channel;
    this.name = name;
  }
  getChannel(channelId) {
    return this.channel.getName(channelId);
  }
}
/**
 * An extension of Event. The ClientEvent simply
 * pre-fixes the name parameter of the event with
 * client. This to comply with Pusher requirements.
 *
 * A ClientEvent will only be used for client-client
 * communication.
 */
class ClientEvent extends Event {
  constructor(name, channel) {
    super("client-" + name, channel);
  }
}
/**
 * SubscriptionSucceededEvent is a typed extension
 * of a base Event. We've enforced
 */
class SubscriptionSucceededEvent extends Event {
  constructor(channel) {
    super('pusher:subscription_succeeded', channel);
  }
}
class SubscriptionErrorEvent extends Event {
  constructor(channel) {
    super('pusher:subscription_error', channel);
  }
}
class MemberAddedEvent extends Event {
  constructor(channel) {
    super('pusher:member_added', channel);
  }
}
class MemberRemovedEvent extends Event {
  constructor(channel) {
    super('pusher:member_removed', channel);
  }
}

/**
 * Channels enable realtime communication between unibuddy servers (BE)
 * and clients (FE). Although channels can be used for essentially any realtime
 * implementation, we use them specifically for use cases such as realtime chat
 * messaging, pinned messaging and presence indicators (how many and who's in a
 * channel).
 *
 * @param name string The name of the channel
 * @param {private: boolean} object Has the channel been set to private or not
 *
 * @method getName Takes a channelId and returns the private pre-fix if selected
 */
class Channel {
  constructor(name, options) {
    if (options === void 0) {
      options = {};
    }
    this.name = name;
    this.private = options.private || false;
  }
  getName(channelId) {
    return "" + (this.private ? 'private-' : '') + (this.name ? this.name + "-" : '') + channelId;
  }
}
/**
 * PresenceChannel is an extension of the Channel class, being true
 * by default and represents
 *
 * @param name string The name of the channel
 * @method getName Takes a channelId and returns the private pre-fix if selected
 */
class PresenceChannel extends Channel {
  constructor(name) {
    super(name, {
      private: true
    });
  }
  getName(channelId) {
    return "presence-" + this.name + "-" + channelId;
  }
}

function useSocketListener(_ref) {
  var {
    event,
    channelId,
    onEvent,
    onConnectionError
  } = _ref;
  var {
    socket,
    ready
  } = useContext(SocketContext);
  var callbackRef = useRef();
  callbackRef.current = onEvent;
  useEffect(() => {
    if (ready && channelId) {
      var cb = function cb() {
        return callbackRef.current(...arguments);
      };
      socket == null || socket.subscribeEvent(event, channelId, cb);
      var subscriptionErrorEvent;
      if (onConnectionError) {
        subscriptionErrorEvent = new SubscriptionErrorEvent(event.channel);
        socket == null || socket.subscribeEvent(subscriptionErrorEvent, channelId, onConnectionError);
      }
      return () => {
        socket == null || socket.unsubscribeEvent(event, channelId, cb);
        if (subscriptionErrorEvent && onConnectionError) {
          socket == null || socket.unsubscribeEvent(subscriptionErrorEvent, channelId, onConnectionError);
        }
      };
    }
  }, [socket, ready, event, channelId, onEvent, onConnectionError]);
}

var usePresenceChannel = _ref => {
  var {
    channel,
    channelId,
    onSub,
    onAdded,
    onRemoved,
    onError
  } = _ref;
  var {
    socket,
    ready
  } = useContext(SocketContext);
  useEffect(() => {
    if (ready && socket) {
      var subscriptionSucceeded = new SubscriptionSucceededEvent(channel);
      var memberRemoved = new MemberRemovedEvent(channel);
      var memberAdded = new MemberAddedEvent(channel);
      var subscriptionError = new SubscriptionErrorEvent(channel);
      socket.subscribeEvent(subscriptionSucceeded, channelId, onSub);
      socket.subscribeEvent(memberRemoved, channelId, onRemoved);
      socket.subscribeEvent(memberAdded, channelId, onAdded);
      if (onError) {
        socket.subscribeEvent(subscriptionError, channelId, onError);
      }
      return () => {
        socket.unsubscribeEvent(subscriptionSucceeded, channelId, onSub);
        socket.unsubscribeEvent(memberRemoved, channelId, onRemoved);
        socket.unsubscribeEvent(memberAdded, channelId, onAdded);
        if (onError) {
          socket.unsubscribeEvent(subscriptionError, channelId, onError);
        }
      };
    }
  }, [socket, ready, channel, channelId, onSub, onAdded, onRemoved, onError]);
};

function _defineProperty(e, r, t) {
  return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
    value: t,
    enumerable: !0,
    configurable: !0,
    writable: !0
  }) : e[r] = t, e;
}
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : i + "";
}

function usePresenceMembers(_ref) {
  var {
    channel,
    channelId
  } = _ref;
  var [members, setMembers] = useState({
    count: 0,
    members: {}
  });
  var [errorStatus, setErrorStatus] = useState([]);
  var onAdded = useCallback(member => {
    setMembers(state => {
      var copy = _objectSpread2(_objectSpread2({}, state.members), {}, {
        [member.id]: null
      });
      return {
        members: copy,
        count: state.count + 1
      };
    });
  }, []);
  var onRemoved = useCallback(member => {
    setMembers(state => {
      var copy = _objectSpread2({}, state.members);
      delete copy[member.id];
      return {
        members: copy,
        count: state.count - 1
      };
    });
  }, []);
  var onSub = useCallback(_ref2 => {
    var {
      count,
      members
    } = _ref2;
    setMembers({
      count: count,
      members: _objectSpread2({}, members)
    });
  }, []);
  var onError = useCallback(_ref3 => {
    var {
      status
    } = _ref3;
    setErrorStatus(status);
  }, []);
  usePresenceChannel({
    channel,
    channelId,
    onSub,
    onRemoved,
    onAdded,
    onError
  });
  return _objectSpread2(_objectSpread2({}, members), {}, {
    errorStatus
  });
}

var noOp = () => {};
/**
 * The useSocketChannel hook exposes a channels trigger method to the client,
 * enabling client-client communication. The hook takes a ClientEvent (an Event
 * implementation of the Event Class pre-fixed with client), the channelId, and
 * three callbacks.
 *
 * onSub: A callback called on pusher subscribe
 * onError: A callback called on a pusher error
 * onEvent: The event to be bound to the trigger method.
 *
 * @returns {trigger: () => {}}
 */
function useSocketChannel(_ref) {
  var {
    channelId,
    event,
    onSub = noOp,
    onError
  } = _ref;
  var {
    socket,
    ready
  } = useContext(SocketContext);
  var [triggerChannel, setTriggerChannel] = useState(undefined);
  useEffect(() => {
    if (ready && channelId) {
      var subscriptionSucceeded = new SubscriptionSucceededEvent(event.channel);
      var channelWithTrigger = socket == null ? void 0 : socket.subscribeEvent(subscriptionSucceeded, channelId, onSub);
      setTriggerChannel(channelWithTrigger);
      var subscriptionError;
      if (onError) {
        subscriptionError = new SubscriptionErrorEvent(event.channel);
        socket == null || socket.subscribeEvent(subscriptionError, channelId, onError);
      }
      return () => {
        if (!channelWithTrigger) return;
        socket == null || socket.unsubscribeEvent(subscriptionSucceeded, channelId, onSub);
        if (onError) {
          socket == null || socket.unsubscribeEvent(subscriptionError, channelId, onError);
        }
      };
    }
  }, [channelId, ready, socket, event, onSub, onError]);
  return triggerChannel;
}

function useSocketData(_ref) {
  var {
    event,
    channelId
  } = _ref;
  var [value, setValue] = useState(undefined);
  var callback = useCallback(socketData => setValue(socketData), [setValue]);
  useSocketListener({
    event,
    channelId,
    onEvent: callback
  });
  return value;
}

export { Channel, ClientEvent, Event, MemberAddedEvent, MemberRemovedEvent, MockSocket, MockedSocketProvider, PresenceChannel, Socket, SocketContext, SocketProvider, SubscriptionErrorEvent, SubscriptionSucceededEvent, usePresenceChannel, usePresenceMembers, useSocketChannel, useSocketData, useSocketListener };
//# sourceMappingURL=sockets.esm.js.map
